/**
 * Theme primitive: a pure module shared by the pre-mount inline script in
 * `index.html` (which duplicates `getInitialTheme` for FOUC prevention) and
 * the React `ThemeProvider`.
 *
 * The theme is reflected on `<html>` as the `data-theme` attribute and the
 * `color-scheme` CSS property. CSS in `src/index.css` selects on
 * `[data-theme="light"]` to swap semantic colour tokens.
 */

export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "ops-dashboard-theme";

function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark";
}

/** Reads the persisted theme from localStorage, if any and valid. */
export function getStoredTheme(): Theme | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Reads the system preference. Defaults to "dark" when matchMedia is missing. */
export function getSystemTheme(): Theme {
  try {
    return window.matchMedia("(prefers-color-scheme: light)").matches
      ? "light"
      : "dark";
  } catch {
    return "dark";
  }
}

/** Storage > system. Falls back to "dark" if both are unavailable. */
export function getInitialTheme(): Theme {
  return getStoredTheme() ?? getSystemTheme();
}

/**
 * Writes `data-theme` and `color-scheme` to `<html>` and persists the choice.
 * Safe in tests: silently no-ops if `document` or `localStorage` are missing.
 */
export function applyTheme(theme: Theme): void {
  try {
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.style.colorScheme = theme;
  } catch {
    /* SSR / jsdom edge — ignore. */
  }
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* private mode / quota — ignore. */
  }
}

/** Reads back what was applied to `<html>` (set by the inline script in
 * `index.html`). Used by the provider so React state matches the painted DOM. */
export function getAppliedTheme(): Theme {
  try {
    const attr = document.documentElement.getAttribute("data-theme");
    return isTheme(attr) ? attr : getInitialTheme();
  } catch {
    return "dark";
  }
}
